import type {
  CartState,
  CartItem,
  AddToCartPayload,
  UpdateQuantityPayload,
  RemoveFromCartPayload,
} from '@model/Cart.model';
import type { ActionWithPayload } from '@model/Store.model';
import {
  CART_ADD_ITEM,
  CART_REMOVE_ITEM,
  CART_SET_QUANTITY,
  CART_CLEAR,
  CART_SET_ALL,
  CART_REMOVE_ITEMS,
} from 'Data/Cart/CartActionTypes';
import type { RemoveManyFromCartPayload } from '@model/Cart.model';
const INITIAL: CartState = {
  itemsById: {},
};

function clampQuantity(nextQty: number, stock?: number, maxPerUser?: number): number {
  const s = Number.isFinite(stock as number) ? Number(stock) : Infinity;
  const m = Number.isFinite(maxPerUser as number) ? Number(maxPerUser) : Infinity;
  const cap = Math.min(s, m);
  if (!isFinite(cap)) return Math.max(0, nextQty);
  return Math.max(0, Math.min(nextQty, Math.max(0, cap)));
}

export const CartReducer = (state: CartState = INITIAL, action: ActionWithPayload): CartState => {
  switch (action.type) {
    case CART_SET_ALL: {
      const payload = (action.payload as { items?: CartItem[] })?.items ?? [];
      const itemsById = (payload as CartItem[]).reduce<Record<string, CartItem>>((acc, it) => {
        if (!it?.id) return acc;
        const qty = clampQuantity(Number(it.quantity || 0), it.stock, it.maxPerUser);
        acc[it.id] = {
          id: it.id,
          name: it.name ?? '',
          price: Number(it.price ?? 0),
          imageUrl: it.imageUrl,
          shortDescription: it.shortDescription,
          stock: it.stock,
          maxPerUser: it.maxPerUser,
          quantity: qty,
        };
        return acc;
      }, {});
      return { ...state, itemsById };
    }
    case CART_REMOVE_ITEMS: {
      const p = action.payload as RemoveManyFromCartPayload;
      const ids = (p?.ids ?? []).filter(Boolean);
      if (!ids.length) return state;

      const next = { ...state.itemsById };
      for (const id of ids) {
        delete next[id];
      }
      return { ...state, itemsById: next };
    }

    case CART_ADD_ITEM: {
      const p = action.payload as AddToCartPayload;
      if (!p?.id) return state;

      const existing = state.itemsById[p.id];
      const baseQty = existing?.quantity ?? 0;

      const nextQty = clampQuantity(
        baseQty + Math.max(0, Number(p.quantity || 0)),
        p.stock ?? existing?.stock,
        p.maxPerUser ?? existing?.maxPerUser
      );

      const nextItem: CartItem = {
        id: p.id,
        name: p.name ?? existing?.name ?? '',
        price: Number(p.price ?? existing?.price ?? 0),
        imageUrl: p.imageUrl ?? existing?.imageUrl,
        shortDescription: p.shortDescription ?? existing?.shortDescription,
        stock: p.stock ?? existing?.stock,
        maxPerUser: p.maxPerUser ?? existing?.maxPerUser,
        quantity: nextQty,
      };

      return { ...state, itemsById: { ...state.itemsById, [p.id]: nextItem } };
    }

    case CART_SET_QUANTITY: {
      const p = action.payload as UpdateQuantityPayload;
      const existing = state.itemsById[p?.id];
      if (!existing) return state;

      const nextQty = clampQuantity(
        Math.max(0, Number(p.quantity || 0)),
        existing.stock,
        existing.maxPerUser
      );

      return {
        ...state,
        itemsById: { ...state.itemsById, [p.id]: { ...existing, quantity: nextQty } },
      };
    }

    case CART_REMOVE_ITEM: {
      const p = action.payload as RemoveFromCartPayload;
      if (!p?.id || !state.itemsById[p.id]) return state;
      const { [p.id]: _removed, ...rest } = state.itemsById;
      return { ...state, itemsById: rest };
    }

    case CART_CLEAR: {
      return INITIAL;
    }

    default:
      return state;
  }
};
